import {
  a,
  h1,
  h2,
  li,
  main,
  p,
  section,
  ul,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from './i18n.js';

/**
 * `/a11y` — public accessibility statement (IV.D.1 / #84).
 *
 * Per `30-design-system.md §10.8.1` commitment 10: "Public a11y
 * statement page with commitments + known gaps + feedback channel."
 *
 * The page is mounted in both:
 *   - The marketing app (`apps/marketing`) so unauthenticated visitors
 *     find it at `https://dungeonlogs.com/a11y`.
 *   - The client app (this file) so an in-product footer link works
 *     without sending the user out of the app — the route renders the
 *     same content, reachable without an authenticated session because
 *     the client shell doesn't gate routes on auth at Phase 1 scope.
 *
 * Layout (#154): a long-form prose surface — a centred reading column
 * (`max-inline-size` in `ch`), an `h1` page title (matching the auditor /
 * inbox pages, which each own their page `h1`), and `<section>`s with
 * `h2` headings. No in-page TOC: at four short sections the page fits
 * within a screen or two, so a TOC would add chrome without aiding
 * navigation (the design-system pattern reserves it for longer prose),
 * and under the client's hash router `#id` anchors collide with the
 * route hash anyway.
 *
 * Last-updated date is bumped manually on any substantive change. CI
 * does NOT regenerate it from git mtime — that would create churn on
 * unrelated edits and obscure the meaningful update signal.
 */

const LAST_UPDATED = '2026-05-22';

/**
 * Generic view of the a11y page. Generic over `Model, Msg` so callers
 * can mount it into any TEA-style component; the content does not bind
 * to any model.
 */
export function viewA11yPage<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
): Mountable {
  return main({ role: 'main', 'data-testid': 'a11y-page', class: 'a11y-page' }, [
    h1({ class: 'a11y-title' }, [t('a11y.heading')]),
    p({ class: 'a11y-last-updated', 'data-testid': 'a11y-last-updated' }, [
      t('a11y.last_updated', { date: LAST_UPDATED }),
    ]),

    section({ class: 'a11y-section', id: 'a11y-conformance' }, [
      h2([t('a11y.conformance_heading')]),
      p([t('a11y.conformance_body')]),
    ]),

    section({ class: 'a11y-section', id: 'a11y-supported' }, [
      h2([t('a11y.supported_at_heading')]),
      ul({ class: 'a11y-bullets' }, [
        li([t('a11y.supported_at.sr_voiceover')]),
        li([t('a11y.supported_at.sr_nvda')]),
        li([t('a11y.supported_at.sr_jaws')]),
        li([t('a11y.supported_at.sr_talkback')]),
        li([t('a11y.supported_at.keyboard')]),
        li([t('a11y.supported_at.reduced_motion')]),
        li([t('a11y.supported_at.text_zoom')]),
      ]),
    ]),

    section({ class: 'a11y-section', id: 'a11y-known-issues' }, [
      h2([t('a11y.known_issues_heading')]),
      ul({ class: 'a11y-bullets' }, [
        li([t('a11y.known_issues.maps')]),
        li([t('a11y.known_issues.belief_layer')]),
      ]),
    ]),

    section({ class: 'a11y-section', id: 'a11y-contact' }, [
      h2([t('a11y.contact_heading')]),
      p({ 'data-testid': 'a11y-contact', class: 'a11y-contact' }, [t('a11y.contact_body')]),
      // A one-click mailto CTA beneath the prose (the address also appears
      // inline in the body copy above; this is the actionable affordance).
      p({ class: 'a11y-contact-cta' }, [
        a({ class: 'a11y-contact-link', href: 'mailto:accessibility@dungeonlogs.com' }, [
          t('a11y.contact_cta'),
        ]),
      ]),
    ]),
  ]);
}

/** The last-updated date — exposed so tests can assert it renders. */
export const A11Y_LAST_UPDATED = LAST_UPDATED;
